import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import {
  Sparkles, User, Lock, ArrowRight, ArrowLeft, Eye, EyeOff,
  ShieldCheck, Bot, CheckCircle2, Zap, Building2, Award
} from 'lucide-react';
import './Login.css';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const { login } = useAuth();
  const { addToast } = useToast();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) {
      addToast('Please enter both username and password.', 'error');
      return;
    }

    setLoading(true);
    try {
      const { role } = await login(username.trim(), password);
      addToast('Welcome back to MentorAI!', 'success');
      if (role === 'SUPER_ADMIN') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      console.error(err);
      addToast(err.response?.data?.detail || 'Invalid username or password. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page-root">
      {/* Background Ambient Glow Orbs */}
      <div className="login-ambient-glow login-glow-1" />
      <div className="login-ambient-glow login-glow-2" />
      <div className="login-ambient-glow login-glow-3" />

      {/* Grid Pattern Overlay */}
      <div className="login-grid-pattern" />

      {/* Top Bar with Back to Home */}
      <header className="login-top-bar">
        <button
          className="login-back-btn"
          onClick={() => navigate('/')}
          title="Return to Landing Page"
        >
          <ArrowLeft size={16} />
          <span>Back to Home</span>
        </button>

        <div className="login-top-brand" onClick={() => navigate('/')}>
          <div className="mini-logo-glow">
            <Sparkles size={16} />
          </div>
          <span>Mentor<strong>AI</strong></span>
        </div>
      </header>

      {/* Main Split Layout Card */}
      <div className="login-wrapper">
        <div className="login-split-card">
          
          {/* Left Column: Form */}
          <div className="login-form-side">
            <div className="login-header-group">
              <div className="login-logo-badge">
                <Sparkles size={28} />
              </div>
              <h1 className="login-main-title">
                Sign in to <span className="title-gradient">MentorAI</span>
              </h1>
              <p className="login-main-subtitle">
                Enter your credentials to access your onboarding dashboard and AI mentors.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="login-interactive-form">
              <div className="login-field-group">
                <label className="login-field-label">
                  <User size={15} /> Username
                </label>
                <div className="login-input-wrapper">
                  <input
                    type="text"
                    required
                    autoFocus
                    autoComplete="username"
                    className="login-custom-input"
                    placeholder="Enter your username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="login-field-group">
                <div className="login-field-header">
                  <label className="login-field-label">
                    <Lock size={15} /> Password
                  </label>
                </div>
                <div className="login-input-wrapper password-input-wrapper">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    required
                    autoComplete="current-password"
                    className="login-custom-input"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                id="login-submit-btn"
                className="btn btn-primary login-btn-animated"
                disabled={loading}
              >
                {loading ? (
                  <span className="login-btn-inner">
                    <span className="login-spinner" />
                    <span>Verifying Credentials...</span>
                  </span>
                ) : (
                  <span className="login-btn-inner">
                    <span>Sign In to Portal</span>
                    <ArrowRight size={18} />
                  </span>
                )}
              </button>
            </form>

            <div className="login-security-notice">
              <ShieldCheck size={16} className="notice-icon" />
              <span>Context-Bounded RAG Architecture • JWT Session Protected</span>
            </div>
          </div>

          {/* Right Column: Visual Showcase & Brand Highlights */}
          <div className="login-showcase-side">
            <div className="showcase-content">
              <div className="showcase-status-pill">
                <span className="status-pulse-dot" />
                <span>AI Workforce Gateway • Live</span>
              </div>

              <h2 className="showcase-heading">
                Accelerate Your Professional Journey from Day One.
              </h2>
              <p className="showcase-body">
                Get instant answers from verified company policies, meet your assigned department mentors, and explore your project workflow repositories.
              </p>

              {/* Showcase Mini Cards */}
              <div className="showcase-feature-list">
                <div className="showcase-feature-item">
                  <div className="feature-item-icon bg-indigo">
                    <Bot size={18} />
                  </div>
                  <div>
                    <strong>24/7 AI Policy Inquiries</strong>
                    <p>Instant answers with direct document citations.</p>
                  </div>
                </div>

                <div className="showcase-feature-item">
                  <div className="feature-item-icon bg-emerald">
                    <Building2 size={18} />
                  </div>
                  <div>
                    <strong>Department AI Leads</strong>
                    <p>Customized orientation tailored to your discipline.</p>
                  </div>
                </div>

                <div className="showcase-feature-item">
                  <div className="feature-item-icon bg-cyan">
                    <Zap size={18} />
                  </div>
                  <div>
                    <strong>Assigned Project Context</strong>
                    <p>Zero knowledge gaps on repository architectures.</p>
                  </div>
                </div>
              </div>

              {/* Bottom Testimonial / Quote Box */}
              <div className="showcase-quote-box">
                <p>
                  "Empowering new hires with instant clarity and specialized AI mentorship."
                </p>
                <div className="quote-author">
                  <Award size={14} />
                  <span>Technaureus Info Solutions</span>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default Login;
